/* Keeping a screen current without a refresh.
 *
 * React re-renders when its own state changes; it has no idea the database
 * moved. Nothing here pushes -- the API is plain request/response -- so a tab
 * is only as fresh as its last fetch. This hook does the two cheap things
 * that cover almost every case:
 *
 *   1. Poll on an interval, but ONLY while the tab is visible. A kitchen
 *      tablet left showing a page keeps up; a tab buried behind twenty others
 *      overnight makes no requests at all.
 *   2. Refetch the moment the tab becomes visible again, so coming back to it
 *      shows current numbers immediately rather than after one more interval.
 *
 * Either half alone leaves a hole: polling only makes you wait when you
 * return, and refetch-on-return never fires for a page nobody touches.
 */
import { useCallback, useEffect, useRef } from "react";

const DEFAULT_INTERVAL_MS = 30_000;

/* Two events can mean "you are back" at once (returning from a minimised
 * window fires both visibilitychange and the timer catching up). Collapsing
 * anything closer together than this keeps that from becoming two round trips
 * for one answer. */
const MIN_GAP_MS = 5_000;

type Options = {
  intervalMs?: number;
  /** Suppress background refreshes -- for a screen whose form fields are
   *  seeded from the same load(), where refetching mid-edit would overwrite
   *  what someone is typing. The first load still happens. */
  paused?: boolean;
};

export function useLiveData(
  load: () => Promise<unknown>,
  { intervalMs = DEFAULT_INTERVAL_MS, paused = false }: Options = {},
) {
  const inFlight = useRef(false);
  const lastRunAt = useRef(0);

  /* A refresh nobody asked for. It yields to one already running and to one
     that just finished, because a slow load() must not stack up behind
     itself on a NAS having a bad morning. Refetching after a mutation is a
     different thing and deliberately does not come through here: that one
     must always run. */
  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    if (Date.now() - lastRunAt.current < MIN_GAP_MS) return;
    inFlight.current = true;
    lastRunAt.current = Date.now();
    try {
      await load();
    } finally {
      inFlight.current = false;
      lastRunAt.current = Date.now();
    }
  }, [load]);

  // First load, whatever else is true. A paused screen still has to render
  // something, and a hidden tab that is about to be shown wants its data.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (paused) return;

    let timer: number | undefined;
    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      timer = window.setInterval(() => void refresh(), intervalMs);
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
        return;
      }
      void refresh();
      start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh, intervalMs, paused]);
}
